import { useState, useEffect } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import API, { formatPrice } from "@/lib/api";
import { useLanguage } from "@/contexts/LanguageContext";
import { Package, ShoppingCart, Users, Tag, DollarSign, Clock } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend } from "recharts";

const CHART_COLORS = ["#8A9A86", "#D5CABD", "#D97706", "#059669", "#6366F1", "#EC4899", "#F59E0B"];

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const { t } = useLanguage();

  useEffect(() => {
    API.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    API.get("/admin/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
    API.get("/orders").then((r) => setRecentOrders(r.data.orders?.slice(0, 5) || [])).catch(() => {});
  }, []);

  const cards = stats ? [
    { label: t("admin_dash.stats.products"), value: stats.total_products, icon: Package, color: "text-[#8A9A86]", id: "products" },
    { label: t("admin_dash.stats.orders"), value: stats.total_orders, icon: ShoppingCart, color: "text-blue-600", id: "orders" },
    { label: t("admin_dash.stats.users"), value: stats.total_users, icon: Users, color: "text-purple-600", id: "users" },
    { label: t("admin_dash.stats.brands"), value: stats.total_brands, icon: Tag, color: "text-amber-600", id: "brands" },
    { label: t("admin_dash.stats.revenue"), value: formatPrice(stats.total_revenue), icon: DollarSign, color: "text-emerald-600", id: "revenue" },
    { label: t("admin_dash.stats.pending"), value: stats.pending_orders, icon: Clock, color: "text-red-500", id: "pending" },
  ] : [];

  return (
    <AdminLayout title={t("admin_dash.title")}>
      <div className="space-y-8" data-testid="admin-dashboard">
        {/* Stats Cards */}
        {stats ? (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            {cards.map((c) => (
              <div key={c.id} className="bg-white border border-stone-100 p-5" data-testid={`stat-${c.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <c.icon className={`w-5 h-5 ${c.color}`} />
                </div>
                <p className="text-2xl font-semibold text-stone-900">{c.value}</p>
                <p className="text-xs text-stone-400 uppercase tracking-wider mt-1">{c.label}</p>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            {[1,2,3,4,5,6].map((i) => <div key={i} className="h-24 bg-stone-100 animate-pulse" />)}
          </div>
        )}

        {/* Charts */}
        {analytics && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Revenue Over Time */}
            {analytics.orders_by_day?.length > 0 && (
              <div className="bg-white border border-stone-100 p-6" data-testid="chart-revenue">
                <h3 className="font-serif text-lg text-stone-900 mb-4">{t("admin_dash.charts.revenue_30")}</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={analytics.orders_by_day}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v) => formatPrice(v)} labelFormatter={(v) => v} />
                    <Line type="monotone" dataKey="revenue" stroke="#8A9A86" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Orders by Day */}
            {analytics.orders_by_day?.length > 0 && (
              <div className="bg-white border border-stone-100 p-6" data-testid="chart-orders">
                <h3 className="font-serif text-lg text-stone-900 mb-4">{t("admin_dash.charts.orders_30")}</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={analytics.orders_by_day}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="orders" fill="#8A9A86" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Revenue by Category */}
            {analytics.revenue_by_category?.length > 0 && (
              <div className="bg-white border border-stone-100 p-6" data-testid="chart-categories">
                <h3 className="font-serif text-lg text-stone-900 mb-4">{t("admin_dash.charts.rev_category")}</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={analytics.revenue_by_category}
                      dataKey="revenue"
                      nameKey="category"
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      label={({ category, percent }) => `${category} (${(percent * 100).toFixed(0)}%)`}
                    >
                      {analytics.revenue_by_category.map((_, i) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => formatPrice(v)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Orders by Status */}
            {analytics.orders_by_status?.length > 0 && (
              <div className="bg-white border border-stone-100 p-6" data-testid="chart-status">
                <h3 className="font-serif text-lg text-stone-900 mb-4">{t("admin_dash.charts.ord_status")}</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={analytics.orders_by_status} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="status" tick={{ fontSize: 11 }} width={80} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#D5CABD" radius={[0, 2, 2, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}

        {/* Top Products */}
        {analytics?.top_products?.length > 0 && (
          <div className="bg-white border border-stone-100 p-6" data-testid="top-products-table">
            <h3 className="font-serif text-lg text-stone-900 mb-4">{t("admin_dash.top_products.title")}</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-stone-100">
                    <th className="text-left p-2 text-xs uppercase tracking-wider text-stone-500 font-semibold">{t("admin_dash.top_products.product")}</th>
                    <th className="text-right p-2 text-xs uppercase tracking-wider text-stone-500 font-semibold">{t("admin_dash.top_products.qty")}</th>
                    <th className="text-right p-2 text-xs uppercase tracking-wider text-stone-500 font-semibold">{t("admin_dash.top_products.revenue")}</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.top_products.map((p, i) => (
                    <tr key={i} className="border-b border-stone-50">
                      <td className="p-2 text-stone-800">{p.name}</td>
                      <td className="p-2 text-right text-stone-600">{p.quantity}</td>
                      <td className="p-2 text-right font-medium text-stone-900">{formatPrice(p.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Recent Orders */}
        <div className="bg-white border border-stone-100 p-6">
          <h2 className="font-serif text-lg text-stone-900 mb-4">{t("admin_dash.recent_orders.title")}</h2>
          {recentOrders.length === 0 ? (
            <p className="text-sm text-stone-400">{t("admin_dash.recent_orders.no_orders")}</p>
          ) : (
            <div className="space-y-3">
              {recentOrders.map((o) => (
                <div key={o.id} className="flex items-center justify-between py-2 border-b border-stone-50 last:border-0">
                  <div>
                    <p className="text-sm font-medium text-stone-800">{o.id}</p>
                    <p className="text-xs text-stone-400">{o.user_email}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-stone-900">{formatPrice(o.total)}</p>
                    <span className="text-[10px] uppercase tracking-wider text-stone-500">{o.status}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
